import { useApolloClient } from '@graphcommerce/graphql'
import { SectionContainer } from '@graphcommerce/next-ui'
import { i18n } from '@lingui/core'
import { Trans } from '@lingui/react'
import { Box, Button, Modal, Paper, Skeleton } from '@mui/material'
import { useCallback, useState } from 'react'
import { StandardButton } from '../../Button/ButtonStyles'
import { SectionTitle } from '../../Container/SectionTitle'
import { ConfirmationPrompt } from '../../Prompt/ConfirmationPrompt'
import { UserSkeleton } from '../../Skeleton/UserSkeleton'
import { Spinner } from '../../Spinner'
import { DeleteSubRoleDocument } from '../SubRoleForm/DeleteSubRole.gql'
import { SubRoleForm } from '../SubRoleForm/SubRoleForm'
import { SubRoleFragment } from './SubRole.gql'

export type SubRolesProps = {
  roles?: SubRoleFragment[]
} & {
  loading: boolean
  refetch: () => void
}

export function SubRoles(props: SubRolesProps) {
  const { roles = [], loading, refetch } = props
  const client = useApolloClient()
  const [selectedRole, setSelectedRole] = useState<null | string | number>(null)
  const [showForm, setShowForm] = useState<boolean>(false)
  const [isDeleting, setIsDeleting] = useState<boolean | number>(false)
  const [displayWarning, setIsDisplayWarning] = useState<boolean>(false)
  const [responseDelete, setResponseDelete] = useState<boolean | undefined>()

  const handleEditRole = (roleId: number) => {
    setSelectedRole(roleId)
    setShowForm(true)
  }

  const handleClickDelete = (roleId: number) => {
    setSelectedRole(roleId)
    setIsDisplayWarning(true)
  }

  const handleRoleDelete = useCallback(
    async (response: boolean, roleId: number) => {
      if (!response) return
      try {
        setIsDeleting(roleId)
        await client.mutate({
          mutation: DeleteSubRoleDocument,
          variables: {
            roleId,
          },
        })
        setIsDeleting(false)
        refetch()
      } catch (error) {
        setIsDeleting(false)
      }
    },
    [client, refetch],
  )

  if (loading) {
    return (
      <div className='w-full'>
        <Paper elevation={3} className='md:px-15 p-10 md:py-8'>
          <span className='Type-XXL-Regular w-full'>Manage roles</span>
          <hr className='mb-10 mt-2 text-20-grey' />
          <UserSkeleton />
        </Paper>
      </div>
    )
  }

  return (
    <Paper elevation={3} className='md:px-15 w-full p-10 md:w-[80%] md:py-8'>
      <span className='Type-XXL-Regular w-full '>Manage roles</span>
      <hr className='mb-[40px] text-20-grey' />
      <div>
        <div className='flex flex-col'>
          {((roles && roles.length === 0) || !roles) && (
            <div>
              <Trans id='You have no roles saved yet' />
            </div>
          )}
          {roles && roles.length >= 1 && (
            <div>
              {roles?.map((role) => {
                if (role && typeof role.role_id === 'number') {
                  return (
                    <div className='min-h-13 flex flex-col items-center justify-between gap-5 border-b-[1px] border-b-20-grey py-[14px] md:flex-row'>
                      <div className='flex w-full items-center justify-start gap-2 md:w-auto md:justify-center md:gap-5'>
                        <span className='Type-XXL-Regular w-full capitalize'>{role.role_name}</span>
                        {isDeleting === role.role_id && <Spinner />}
                      </div>
                      <div className='grid w-full grid-cols-2 items-center justify-center gap-5 md:flex md:w-auto'>
                        <Button
                          color='primary'
                          className='max-w-[350px] bg-pure-white uppercase'
                          variant='outlined'
                          onClick={() => handleEditRole(Number(role.role_id))}
                        >
                          {i18n._('Edit')}
                        </Button>
                        <Button
                          color='primary'
                          className='max-w-[350px] bg-pure-white uppercase'
                          variant='outlined'
                          onClick={() => handleClickDelete(Number(role.role_id))}
                        >
                          {i18n._('Delete')}
                        </Button>
                      </div>
                    </div>
                  )
                }
                return null
              })}
            </div>
          )}
        </div>
        <div className='mt-11 flex w-full flex-col items-center justify-center gap-[30px] md:flex-row md:justify-start'>
          <StandardButton
            type='button'
            className='w-full max-w-[350px] bg-material-ui-blue uppercase md:w-auto'
            variant='contained'
            onClick={() => {
              setSelectedRole(null)
              setShowForm(true)
            }}
            size='small'
          >
            {i18n._('Add New Role')}
          </StandardButton>
          {/* <Button
            disabled={!selectedRole}
            color='white'
            label='Delete'
            onClick={() => setIsDisplayWarning(true)}
          /> */}
        </div>
      </div>

      <Modal onClose={() => setShowForm(false)} open={!!showForm} className='z-10'>
        <div className='absolute left-[5vw] top-[5vh] w-[90vw] overflow-auto rounded-md border-solid bg-pure-white lg:left-[25vw] lg:w-[50vw]'>
          {selectedRole && roles && roles.find((role) => role?.role_id === selectedRole) ? (
            <div className='max-h-[500px] overflow-y-auto p-10 px-10 py-6'>
              <SectionTitle title='Edit Role' />

              <SubRoleForm
                role={
                  roles.find((role) => role?.role_id === selectedRole) as unknown as SubRoleFragment
                }
                onSuccess={() => {
                  setShowForm(false)
                  refetch()
                }}
                onCancel={() => setShowForm(false)}
              />
            </div>
          ) : null}
          {!selectedRole && (
            <div className='max-h-[500px] overflow-y-auto p-10 px-10 py-6'>
              <SectionTitle title='Add new role' />

              <SubRoleForm
                onSuccess={() => {
                  setShowForm(false)
                  refetch()
                }}
                onComplete={() => {}}
                onCancel={() => setShowForm(false)}
              />
            </div>
          )}
        </div>
      </Modal>
      <ConfirmationPrompt
        title={i18n._('Delete Role')}
        message={i18n._('Are you sure that you want to delete this role?')}
        isDisplayed={displayWarning}
        setIsDisplayed={setIsDisplayWarning}
        setResponse={setResponseDelete}
        callback={handleRoleDelete}
        callbackArgs={selectedRole as number}
      />
    </Paper>
  )
}
